/**
 * mSTEM Project Detail Modal (PRJ-02 S-2) — "Thông tin chi tiết dự án".
 *
 * LLD UC "Tải các dự án đã có", luồng con S-2 "Xem thông tin các dự án đã tạo":
 * the list answers "tất cả các dự án đã lưu", this modal answers "hiển thị
 * thông tin chi tiết của các dự án" for the row the student picks with ℹ.
 *
 * Read-only presentational modal opened from the Open Project row action ℹ.
 * Two stacked panels:
 *   Panel A — project identity grid (mã dự án, loại thiết bị, thiết bị, tác
 *             giả, lớp, ngày tạo, sửa lần cuối, mở lần cuối, lượt mở, mô tả)
 *   Panel B — content statistics (khối lệnh / nhân vật / phông nền / biến / KB)
 *
 * MOCK-ONLY: the list fields come from the myProjects reducer (seeded by
 * src/lib/mstem-mock/projects.js) and the detail-only fields from
 * src/lib/mstem-mock/project-details.js — no project file is ever parsed. The
 * container merges both and passes one `project` object so this stays a dumb
 * renderer.
 *
 * Empty-value policy (UI-SPEC Pitfall 4 — never-empty surface): every missing
 * field renders as EMPTY_VALUE ('—') instead of collapsing the row, so a
 * user-created project with no detail record still fills the surface.
 *
 * NOC-navy header band + white body, mirroring KitDetailModal (SMH-04).
 */
import PropTypes from 'prop-types';
import React from 'react';
import {defineMessages, FormattedMessage, injectIntl, intlShape} from 'react-intl';

import Modal from '../../containers/modal.jsx';
import MstemBadge from '../mstem-badge/mstem-badge.jsx';

import styles from './project-detail-modal.css';

const EMPTY_VALUE = '—';

const messages = defineMessages({
    title: {
        defaultMessage: 'Thông tin chi tiết dự án',
        description: 'Project detail modal title (PRJ-02 S-2)',
        id: 'gen.project.detail.title'
    },
    infoSection: {
        defaultMessage: 'Thông tin dự án',
        description: 'Project detail — identity panel head',
        id: 'gen.project.detail.section.info'
    },
    statsSection: {
        defaultMessage: 'Thống kê nội dung',
        description: 'Project detail — content statistics panel head',
        id: 'gen.project.detail.section.stats'
    },
    labelId: {
        defaultMessage: 'Mã dự án',
        description: 'Project detail field — project id',
        id: 'gen.project.detail.label.id'
    },
    labelType: {
        defaultMessage: 'Loại thiết bị',
        description: 'Project detail field — device type',
        id: 'gen.project.detail.label.type'
    },
    labelDeviceModel: {
        defaultMessage: 'Thiết bị cấu hình',
        description: 'Project detail field — configured device model',
        id: 'gen.project.detail.label.deviceModel'
    },
    labelAuthor: {
        defaultMessage: 'Tác giả',
        description: 'Project detail field — author',
        id: 'gen.project.detail.label.author'
    },
    labelClass: {
        defaultMessage: 'Lớp',
        description: 'Project detail field — class name',
        id: 'gen.project.detail.label.class'
    },
    labelCreated: {
        defaultMessage: 'Ngày tạo',
        description: 'Project detail field — created date',
        id: 'gen.project.detail.label.created'
    },
    labelUpdated: {
        defaultMessage: 'Sửa lần cuối',
        description: 'Project detail field — last modified date',
        id: 'gen.project.detail.label.updated'
    },
    labelLastOpened: {
        defaultMessage: 'Mở lần cuối',
        description: 'Project detail field — last opened date',
        id: 'gen.project.detail.label.lastOpened'
    },
    labelOpenCount: {
        defaultMessage: 'Số lượt mở',
        description: 'Project detail field — open count',
        id: 'gen.project.detail.label.openCount'
    },
    labelDescription: {
        defaultMessage: 'Mô tả',
        description: 'Project detail field — description',
        id: 'gen.project.detail.label.description'
    },
    statBlocks: {
        defaultMessage: 'Khối lệnh',
        description: 'Project detail stat — block count',
        id: 'gen.project.detail.stat.blocks'
    },
    statSprites: {
        defaultMessage: 'Nhân vật',
        description: 'Project detail stat — sprite count',
        id: 'gen.project.detail.stat.sprites'
    },
    statBackdrops: {
        defaultMessage: 'Phông nền',
        description: 'Project detail stat — backdrop count',
        id: 'gen.project.detail.stat.backdrops'
    },
    statVariables: {
        defaultMessage: 'Biến',
        description: 'Project detail stat — variable count',
        id: 'gen.project.detail.stat.variables'
    },
    statSize: {
        defaultMessage: 'Dung lượng (KB)',
        description: 'Project detail stat — size in KB',
        id: 'gen.project.detail.stat.size'
    },
    visibilityShared: {
        defaultMessage: 'Đã chia sẻ',
        description: 'Project visibility badge — shared',
        id: 'gen.project.detail.visibility.shared'
    },
    visibilityPrivate: {
        defaultMessage: 'Riêng tư',
        description: 'Project visibility badge — private',
        id: 'gen.project.detail.visibility.private'
    },
    loadButton: {
        defaultMessage: 'Tải dự án',
        description: 'Project detail — load this project',
        id: 'gen.project.detail.load'
    },
    close: {
        defaultMessage: 'Đóng',
        description: 'Project detail close button',
        id: 'gen.project.detail.close'
    }
});

// Device-type display labels — ids already registered by new-project-modal.jsx,
// referenced (not re-declared) to satisfy the "No duplicate gen.* ids" test.
const TYPE_LABEL_IDS = {
    robot: {id: 'gen.project.type.robot', defaultMessage: 'Robot mSTEM'},
    kit: {id: 'gen.project.type.kit', defaultMessage: 'Kit SmartHome'},
    none: {id: 'gen.project.type.none', defaultMessage: 'Không kết nối thiết bị'}
};

const formatDate = function (iso) {
    if (!iso) return EMPTY_VALUE;
    const d = new Date(iso);
    if (isNaN(d.getTime())) return EMPTY_VALUE;
    const dd = String(d.getDate()).padStart(2, '0');
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    return `${dd}/${mm}/${d.getFullYear()}`;
};

const displayValue = function (value) {
    if (typeof value === 'number') return value;
    if (typeof value === 'string' && value.trim()) return value;
    return EMPTY_VALUE;
};

const ProjectDetailModalComponent = ({intl, project, isLoading, onLoad, onRequestClose}) => {
    const typeDescriptor = TYPE_LABEL_IDS[project.type] || TYPE_LABEL_IDS.none;
    const isShared = project.visibility === 'shared';

    const rows = [
        {key: 'id', label: messages.labelId, value: project.id},
        {key: 'type', label: messages.labelType, value: intl.formatMessage(typeDescriptor)},
        {key: 'deviceModel', label: messages.labelDeviceModel, value: project.deviceModel},
        {key: 'author', label: messages.labelAuthor, value: project.author},
        {key: 'class', label: messages.labelClass, value: project.className},
        {key: 'created', label: messages.labelCreated, value: formatDate(project.createdAt)},
        {key: 'updated', label: messages.labelUpdated, value: formatDate(project.updatedAt)},
        {key: 'lastOpened', label: messages.labelLastOpened, value: formatDate(project.lastOpenedAt)},
        {key: 'openCount', label: messages.labelOpenCount, value: project.openCount}
    ];

    const stats = [
        {key: 'blocks', label: messages.statBlocks, value: project.blockCount},
        {key: 'sprites', label: messages.statSprites, value: project.spriteCount},
        {key: 'backdrops', label: messages.statBackdrops, value: project.backdropCount},
        {key: 'variables', label: messages.statVariables, value: project.variableCount},
        {key: 'size', label: messages.statSize, value: project.sizeKB}
    ];

    return (
        <Modal
            className={styles.modalContent}
            contentLabel={intl.formatMessage(messages.title)}
            headerClassName={styles.header}
            id="projectDetailModal"
            onRequestClose={onRequestClose}
        >
            <div className={styles.body}>
                <div className={styles.titleRow}>
                    <span className={styles.projectName}>{displayValue(project.name)}</span>
                    <MstemBadge variant={isShared ? 'info' : 'primary'}>
                        <FormattedMessage
                            {...(isShared ? messages.visibilityShared : messages.visibilityPrivate)}
                        />
                    </MstemBadge>
                </div>

                {/* Panel A — project identity */}
                <div className={styles.panel}>
                    <p className={styles.sectionHead}>
                        <FormattedMessage {...messages.infoSection} />
                    </p>
                    <div className={styles.infoGrid}>
                        {rows.map(row => (
                            <div
                                key={row.key}
                                className={styles.infoItem}
                            >
                                <span className={styles.infoLabel}>
                                    <FormattedMessage {...row.label} />
                                </span>
                                <span className={styles.infoValue}>{displayValue(row.value)}</span>
                            </div>
                        ))}
                        <div className={styles.infoItemWide}>
                            <span className={styles.infoLabel}>
                                <FormattedMessage {...messages.labelDescription} />
                            </span>
                            <span className={styles.infoValue}>{displayValue(project.description)}</span>
                        </div>
                    </div>
                </div>

                {/* Panel B — content statistics */}
                <div className={styles.panel}>
                    <p className={styles.sectionHead}>
                        <FormattedMessage {...messages.statsSection} />
                    </p>
                    <div className={styles.statStrip}>
                        {stats.map(stat => (
                            <div
                                key={stat.key}
                                className={styles.statTile}
                            >
                                <span className={styles.statValue}>{displayValue(stat.value)}</span>
                                <span className={styles.statLabel}>
                                    <FormattedMessage {...stat.label} />
                                </span>
                            </div>
                        ))}
                    </div>
                </div>

                <div className={styles.footer}>
                    <button
                        className={styles.closeButton}
                        type="button"
                        onClick={onRequestClose}
                    >
                        <FormattedMessage {...messages.close} />
                    </button>
                    <button
                        className={styles.loadButton}
                        disabled={isLoading}
                        type="button"
                        onClick={onLoad}
                    >
                        <FormattedMessage {...messages.loadButton} />
                    </button>
                </div>
            </div>
        </Modal>
    );
};

ProjectDetailModalComponent.propTypes = {
    intl: intlShape.isRequired,
    isLoading: PropTypes.bool,
    onLoad: PropTypes.func.isRequired,
    onRequestClose: PropTypes.func.isRequired,
    // List fields (from the myProjects reducer) + detail-only fields (merged in
    // by the container from src/lib/mstem-mock/project-details.js). Every field
    // is optional: a user-created project has no detail record and renders '—'.
    project: PropTypes.shape({
        author: PropTypes.string,
        backdropCount: PropTypes.number,
        blockCount: PropTypes.number,
        className: PropTypes.string,
        createdAt: PropTypes.string,
        description: PropTypes.string,
        deviceModel: PropTypes.string,
        id: PropTypes.string,
        lastOpenedAt: PropTypes.string,
        name: PropTypes.string,
        openCount: PropTypes.number,
        sizeKB: PropTypes.number,
        spriteCount: PropTypes.number,
        type: PropTypes.string,
        updatedAt: PropTypes.string,
        variableCount: PropTypes.number,
        visibility: PropTypes.string
    }).isRequired
};

ProjectDetailModalComponent.defaultProps = {
    isLoading: false
};

export default injectIntl(ProjectDetailModalComponent);
